import { LINE_TYPES } from '../firebase/schema';
import { sumTaxableFeesAmount, resolveTaxExempt, DEFAULT_SALES_TAX_RATE } from './orderSalesTax';
import { buildPaymentBreakdown, discountLabelFromOrder, emptyPaymentBreakdown } from './paymentBreakdown';

export const DEFAULT_UPCHARGE_PRICE = 2;
export const UPCHARGE_SIZES = ['2XL', '3XL', '4XL', '5XL', '6XL', '7XL'];

export function resolveUpchargePrice(order) {
  if (
    order?.upchargePrice === '' ||
    order?.upchargePrice == null ||
    Number.isNaN(parseFloat(order.upchargePrice))
  ) {
    return DEFAULT_UPCHARGE_PRICE;
  }
  return parseFloat(order.upchargePrice);
}

export function lineQuantity(line) {
  if (line?.totalQuantity != null) return Number(line.totalQuantity) || 0;
  return Object.values(line?.sizes || {}).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
}

export function isDesignOnlyLine(line) {
  return line?.type === LINE_TYPES.DESIGN_ONLY || line?.type === 'Design Only';
}

export function computeUpchargeQuantity(line) {
  if (!line || isDesignOnlyLine(line)) return 0;
  const sizes = line.sizes || {};
  return UPCHARGE_SIZES.reduce((sum, size) => sum + (Number(sizes[size]) || 0), 0);
}

export function computeUpchargeTotal(lineItems = [], upchargePrice = DEFAULT_UPCHARGE_PRICE) {
  const amount = Number(upchargePrice) || 0;
  if (!amount) return 0;
  return lineItems.reduce((sum, line) => sum + computeUpchargeQuantity(line) * amount, 0);
}

export function getOrderFees(order) {
  if (!order) return [];

  if (Array.isArray(order.fees) && order.fees.length > 0) {
    return order.fees;
  }

  const fees = [];
  if (order.fee1Type && (order.fee1Quantity || order.fee1Price)) {
    fees.push({
      id: 'fee1',
      type: order.fee1Type,
      quantity: order.fee1Quantity || 1,
      price: order.fee1Price || 0,
      taxable: true
    });
  }
  if (order.fee2Type && (order.fee2Quantity || order.fee2Price)) {
    fees.push({
      id: 'fee2',
      type: order.fee2Type,
      quantity: order.fee2Quantity || 1,
      price: order.fee2Price || 0,
      taxable: true
    });
  }
  return fees;
}

/**
 * Invoice-aligned document totals: line prices + 2XL+ upcharges + fees - discount + tax.
 */
export function computeOrderDocumentTotals(order, lineItems = [], {
  customer = null,
  invoiceTaxExempt,
  fees
} = {}) {
  if (!order) {
    const empty = emptyPaymentBreakdown();
    return {
      baseSubtotal: 0,
      upchargeTotal: 0,
      subtotal: 0,
      feesTotal: 0,
      taxableFeesTotal: 0,
      discountAmount: 0,
      tax: 0,
      total: 0,
      isTaxExempt: false,
      orderFees: [],
      taxableSubtotal: empty.taxableSubtotal,
      nontaxableSubtotal: empty.nontaxableSubtotal,
      taxRate: empty.taxRate,
      paymentBreakdown: empty
    };
  }

  const orderFees = fees ?? getOrderFees(order);
  const upchargePrice = resolveUpchargePrice(order);
  const baseSubtotal = lineItems.reduce(
    (sum, line) => sum + lineQuantity(line) * (parseFloat(line.pricePerPiece) || 0),
    0
  );
  const upchargeTotal = computeUpchargeTotal(lineItems, upchargePrice);
  const subtotal = baseSubtotal + upchargeTotal;

  const feesTotal = orderFees.reduce((sum, fee) => {
    const quantity = parseFloat(fee.quantity) || 0;
    const price = parseFloat(fee.price) || 0;
    return sum + quantity * price;
  }, 0);
  const taxableFeesTotal = sumTaxableFeesAmount(orderFees);

  let discountAmount = 0;
  if (order.discount && order.discount.amount) {
    const discountBase = subtotal + feesTotal;
    if (order.discount.type === 'percent') {
      discountAmount = discountBase * (parseFloat(order.discount.amount) / 100);
    } else {
      discountAmount = parseFloat(order.discount.amount) || 0;
    }
  }

  const isTaxExempt = resolveTaxExempt({
    order,
    customer,
    invoiceTaxExempt: invoiceTaxExempt !== undefined ? invoiceTaxExempt : order.invoiceTaxExempt
  });

  const { amountDue, breakdown, discountAmount: roundedDiscount } = buildPaymentBreakdown({
    subtotal,
    feesTotal,
    taxableFeesTotal,
    discountAmount,
    isTaxExempt,
    taxRate: DEFAULT_SALES_TAX_RATE,
    discountLabel: discountLabelFromOrder(order)
  });

  return {
    baseSubtotal,
    upchargeTotal,
    subtotal,
    feesTotal,
    taxableFeesTotal,
    discountAmount: roundedDiscount,
    tax: breakdown.taxAmount,
    total: amountDue,
    isTaxExempt,
    orderFees,
    taxableSubtotal: breakdown.taxableSubtotal,
    nontaxableSubtotal: breakdown.nontaxableSubtotal,
    taxRate: breakdown.taxRate,
    paymentBreakdown: breakdown
  };
}
